/**
 * VOB — Volume-Trend Order Block Engine.
 *
 * Faithful port of the user's Pine v6 "Volume-Trend Order Block Engine":
 *
 *  1. Custom Supertrend — a trailing stop built from a volatility band
 *     hl2 ± mult · SMA(high−low, len); `market_trend` flips when close crosses
 *     the prior stop. A cloud fills between close and the stop line.
 *  2. Order Blocks — on a confirmed swing pivot (pivotLen bars each side) and
 *     while in the matching trend, a rectangle is drawn from the pivot bar,
 *     split into a top "Sell %" sub-box and a bottom "Buy %" sub-box by the
 *     buy/sell volume ratio over the last pivotLen+1 bars. A new OB can't
 *     overlap the active one; when superseded the prior OB freezes; the active
 *     OB extends right and (optionally) deletes when price fully breaks it.
 *  3. Retests — price crossing back above a bull OB top / below a bear OB
 *     bottom, gated by a minimum buy/sell-% filter.
 *
 * Computed on CLOSED RAW candles (repaint-safe; pivots confirm pivotLen bars
 * after the swing). Independent of the chart's display candle type.
 */
import { CandleRow } from '../components/charts/TradingChart';

export interface VobParams {
  stLen: number;
  stMult: number;
  pivotLen: number;
  deleteOnBreak: boolean;
  showBullRetest: boolean;
  showBearRetest: boolean;
  bullVolPct: number;   // 0..100
  bearVolPct: number;   // 0..100
}

export interface VobOrderBlock {
  leftTime: number;
  rightTime: number;
  top: number;
  bottom: number;
  splitPrice: number;   // boundary between Sell (top) and Buy (bottom) halves
  buyPct: number;       // 0..100 (rounded)
  sellPct: number;      // 0..100 (rounded)
  trend: 1 | -1;        // 1 = bullish OB, -1 = bearish OB
}

export interface VobTrendPoint { time: number; value: number; dir: 1 | -1; }
export interface VobCloudPoint { time: number; upper: number; lower: number; trend: 1 | -1; }

export interface VobResult {
  /** Single continuous trend-stop line — one point per bar, coloured by `dir`
   *  (1 = bullish, -1 = bearish). Rendered as ONE series via per-point colour. */
  trend: VobTrendPoint[];
  /** Per-bar cloud band (between close and the stop line). */
  cloud: VobCloudPoint[];
  /** Order blocks (oldest→newest), frozen historical + the live one. */
  orderBlocks: VobOrderBlock[];
  /** Retest cross times. */
  retests: { buys: number[]; shorts: number[] };
}

/** Simple moving average over a numeric series (null until `len` samples). */
function sma(src: number[], len: number): Array<number | null> {
  const out: Array<number | null> = new Array(src.length).fill(null);
  let sum = 0;
  for (let i = 0; i < src.length; i++) {
    sum += src[i];
    if (i >= len) sum -= src[i - len];
    if (i >= len - 1) out[i] = sum / len;
  }
  return out;
}

const MAX_BLOCKS = 250;

export function computeVob(candles: CandleRow[], params: VobParams): VobResult {
  const empty: VobResult = { trend: [], cloud: [], orderBlocks: [], retests: { buys: [], shorts: [] } };
  const n = candles.length;
  const stLen = Math.max(1, Math.floor(params.stLen) || 50);
  const mult = Number(params.stMult) || 3.5;
  const pivotLen = Math.max(1, Math.floor(params.pivotLen) || 7);
  if (n < stLen + 2 || n < pivotLen * 2 + 2) return empty;

  const high = candles.map(c => c.high);
  const low = candles.map(c => c.low);
  const open = candles.map(c => c.open);
  const close = candles.map(c => c.close);
  const vol = candles.map(c => (c.volume && c.volume > 0 ? c.volume : 0));
  const time = candles.map(c => c.time as number);
  const range = candles.map(c => c.high - c.low);
  const atr = sma(range, stLen);

  // ── Custom Supertrend (faithful to the Pine evaluation order) ──
  const trendStop: number[] = new Array(n).fill(NaN);
  const marketTrend: number[] = new Array(n).fill(1);
  let prevStop = NaN;
  let prevTrend = 1;
  for (let i = 0; i < n; i++) {
    const a = atr[i];
    const hl2 = (high[i] + low[i]) / 2;
    const upper = a == null ? NaN : hl2 + mult * a;
    const lower = a == null ? NaN : hl2 - mult * a;
    let mt = prevTrend;
    // First assignment using the CURRENT (carried) trend + prior stop.
    let stop = mt === 1
      ? Math.max(lower, Number.isFinite(prevStop) ? prevStop : lower)
      : Math.min(upper, Number.isFinite(prevStop) ? prevStop : upper);
    // Flip check against the PRIOR stop.
    if (i > 0 && Number.isFinite(prevStop)) {
      if (close[i] > prevStop && close[i - 1] <= prevStop) { mt = 1; stop = lower; }
      else if (close[i] < prevStop && close[i - 1] >= prevStop) { mt = -1; stop = upper; }
    }
    marketTrend[i] = mt;
    trendStop[i] = stop;
    prevStop = stop;
    prevTrend = mt;
  }

  // ── Pivots (strict, pivotLen each side; confirmed at bar center+pivotLen) ──
  const isPivotLow = (c: number): boolean => {
    if (c - pivotLen < 0 || c + pivotLen >= n) return false;
    const v = low[c];
    for (let k = c - pivotLen; k <= c + pivotLen; k++) { if (k !== c && low[k] <= v) return false; }
    return true;
  };
  const isPivotHigh = (c: number): boolean => {
    if (c - pivotLen < 0 || c + pivotLen >= n) return false;
    const v = high[c];
    for (let k = c - pivotLen; k <= c + pivotLen; k++) { if (k !== c && high[k] >= v) return false; }
    return true;
  };

  // Buy-volume ratio over the last pivotLen+1 bars from the confirming bar i.
  const windowBuyRatio = (i: number): number => {
    let buy = 0, sell = 0;
    for (let k = 0; k <= pivotLen; k++) {
      const j = i - k;
      if (j < 0) break;
      if (close[j] >= open[j]) buy += vol[j]; else sell += vol[j];
    }
    const tot = buy + sell;
    return tot > 0 ? buy / tot : 0.5;
  };

  // ── Order Block engine ──
  const obs: VobOrderBlock[] = [];
  let activeIdx = -1;
  let aTop = NaN, aBot = NaN, aTrend = 0, aBuyRatio = NaN;
  // Per-bar snapshot of the active OB state (for crossover/retest detection).
  const actTopArr: number[] = new Array(n).fill(NaN);
  const actBotArr: number[] = new Array(n).fill(NaN);
  const actBuyArr: number[] = new Array(n).fill(NaN);

  const overlaps = (top: number, bot: number) =>
    Number.isFinite(aTop) && Number.isFinite(aBot) && !(bot > aTop || top < aBot);

  for (let i = 0; i < n; i++) {
    const c = i - pivotLen;          // pivot center bar
    const a = atr[i];

    // 1. Bullish OB (uptrend + confirmed swing low).
    if (marketTrend[i] === 1 && a != null && c >= 0 && isPivotLow(c)) {
      const top = Math.min(open[c], close[c]);
      const bot = top - a;
      if (!overlaps(top, bot)) {
        if (activeIdx >= 0) obs[activeIdx].rightTime = time[c];
        const buyRatio = windowBuyRatio(i);
        const split = bot + (top - bot) * buyRatio;
        obs.push({
          leftTime: time[c], rightTime: time[n - 1], top, bottom: bot, splitPrice: split,
          buyPct: Math.round(buyRatio * 100), sellPct: Math.round((1 - buyRatio) * 100), trend: 1,
        });
        activeIdx = obs.length - 1;
        aTop = top; aBot = bot; aTrend = 1; aBuyRatio = buyRatio;
      }
    }

    // 2. Bearish OB (downtrend + confirmed swing high).
    if (marketTrend[i] === -1 && a != null && c >= 0 && isPivotHigh(c)) {
      const bot = Math.max(open[c], close[c]);
      const top = bot + a;
      if (!overlaps(top, bot)) {
        if (activeIdx >= 0) obs[activeIdx].rightTime = time[c];
        const buyRatio = windowBuyRatio(i);
        const split = bot + (top - bot) * buyRatio;
        obs.push({
          leftTime: time[c], rightTime: time[n - 1], top, bottom: bot, splitPrice: split,
          buyPct: Math.round(buyRatio * 100), sellPct: Math.round((1 - buyRatio) * 100), trend: -1,
        });
        activeIdx = obs.length - 1;
        aTop = top; aBot = bot; aTrend = -1; aBuyRatio = buyRatio;
      }
    }

    // 3. Delete on complete break (active OB only).
    if (params.deleteOnBreak && activeIdx >= 0 && Number.isFinite(aTop) && Number.isFinite(aBot)) {
      const broken = (aTrend === 1 && high[i] < aBot) || (aTrend === -1 && low[i] > aTop);
      if (broken) {
        obs.splice(activeIdx, 1);   // remove the active OB; frozen ones remain
        activeIdx = -1; aTop = NaN; aBot = NaN; aTrend = 0; aBuyRatio = NaN;
      }
    }

    actTopArr[i] = aTop; actBotArr[i] = aBot; actBuyArr[i] = aBuyRatio;
  }
  // Keep the live OB extending to the last bar.
  if (activeIdx >= 0) obs[activeIdx].rightTime = time[n - 1];

  // ── Retest signals (second pass; uses the per-bar active snapshot) ──
  const buys: number[] = [];
  const shorts: number[] = [];
  const bullThr = params.bullVolPct / 100;
  const bearThr = params.bearVolPct / 100;
  for (let i = 1; i < n; i++) {
    const flip = marketTrend[i] !== marketTrend[i - 1];
    if (flip) continue;
    // A pivot confirmed on this bar suppresses a retest (Pine na(pivot_*)).
    const pivotHere = (i - pivotLen >= 0) && (isPivotLow(i - pivotLen) || isPivotHigh(i - pivotLen));
    if (pivotHere) continue;
    const top = actTopArr[i], topPrev = actTopArr[i - 1];
    const bot = actBotArr[i], botPrev = actBotArr[i - 1];
    const buyRatio = actBuyArr[i];
    if (params.showBullRetest && Number.isFinite(top) && Number.isFinite(topPrev) && buyRatio >= bullThr) {
      if (low[i] > top && low[i - 1] <= topPrev) buys.push(time[i]);   // crossover(low, activeTop)
    }
    if (params.showBearRetest && Number.isFinite(bot) && Number.isFinite(botPrev) && (1 - buyRatio) >= bearThr) {
      if (high[i] < bot && high[i - 1] >= botPrev) shorts.push(time[i]); // crossunder(high, activeBot)
    }
  }

  // ── Single continuous trend-stop line (coloured per-point by trend) + cloud ──
  const trend: VobTrendPoint[] = [];
  const cloud: VobCloudPoint[] = [];
  for (let i = 0; i < n; i++) {
    const s = trendStop[i];
    if (!Number.isFinite(s)) continue;
    const t = marketTrend[i] as 1 | -1;
    trend.push({ time: time[i], value: s, dir: t });
    cloud.push({ time: time[i], upper: Math.max(close[i], s), lower: Math.min(close[i], s), trend: t });
  }

  return {
    trend, cloud,
    orderBlocks: obs.slice(-MAX_BLOCKS),
    retests: { buys, shorts },
  };
}
